import { useGameStore } from "../stores/gameStore.js";
import { useInventoryStore } from "../stores/inventoryStore.js";
import { useUpgradeStore } from "../stores/upgradeStore.js";
import { useWorkflowStore } from "../stores/workflowStore.js";
import { Creature } from "./classes/Creature.js";
import { StatusEffect } from "./classes/StatusEffect.js";
import { Weapon } from "./classes/Weapon.js";
import { Materials } from "./data/materials.js";
import { WeaponSubtypes } from "./data/weaponSubtypes.js";

const SAVE_KEY = "knightstein_save";

const stores = [
  { name: "game", use: useGameStore },
  { name: "inventory", use: useInventoryStore },
  { name: "workflow", use: useWorkflowStore },
  { name: "upgrades", use: useUpgradeStore },
];

function serializeWeapon(weapon) {
  if (!weapon) return null;
  return {
    materialName: weapon.material.name,
    subtypeName: weapon.subtype.name,
    rolls: weapon.rolls,
  };
}

function reconstructWeapon(data) {
  if (!data) return null;
  const materialName = data.materialName || data.material?.name;
  const subtypeName = data.subtypeName || data.subtype?.name;
  const material = Object.values(Materials).find((m) => m.name === materialName);
  const subtype = Object.values(WeaponSubtypes).find((s) => s.name === subtypeName);
  if (!material || !subtype) {
    console.error("Failed to reconstruct weapon:", data, "material:", material, "subtype:", subtype);
    return null;
  }
  const name = `${material.name} ${subtype.name}`;
  return new Weapon(name, material, subtype, data.rolls);
}

function reconstructStatusEffect(data) {
  if (!data) return null;
  return new StatusEffect(data);
}

function reconstructCreature(data) {
  if (!data) return null;
  const weapon = reconstructWeapon(data.weapon);
  const creature = new Creature(
    data.name,
    data.maxHealth,
    weapon,
    data.armor || 0,
    data.isPlayer || false
  );
  creature.health = data.health;
  creature.timer = data.timer || 0;
  creature.currentAmmo = data.currentAmmo || 0;
  creature.isReloading = data.isReloading || false;
  creature.statusEffects = (data.statusEffects || []).map(reconstructStatusEffect);
  return creature;
}

function serializeCreature(creature) {
  if (!creature) return null;
  return {
    ...creature,
    weapon: serializeWeapon(creature.weapon),
  };
}

export function saveGame() {
  const gameStore = useGameStore();
  const inventoryStore = useInventoryStore();
  const workflowStore = useWorkflowStore();
  const upgradeStore = useUpgradeStore();

  const saveData = {
    game: {
      ...gameStore.$state,
      player: serializeCreature(gameStore.player),
      enemy: serializeCreature(gameStore.enemy),
    },
    inventory: {
      materials: inventoryStore.materials,
      items: inventoryStore.items.map(serializeWeapon),
    },
    workflow: {
      workflows: workflowStore.workflows,
      smithingQueue: workflowStore.smithingQueue,
      smeltingQueue: workflowStore.smeltingQueue.map(serializeWeapon),
    },
    upgrades: {
      purchased: upgradeStore.purchased,
    },
  };

  localStorage.setItem(SAVE_KEY, JSON.stringify(saveData));
}

export function loadGame() {
  const saved = localStorage.getItem(SAVE_KEY);
  if (!saved) return false;

  try {
    const saveData = JSON.parse(saved);

    for (const store of stores) {
      if (!saveData[store.name]) {
        deleteSave();
        return false;
      }
    }

    const gameStore = useGameStore();
    const gameData = saveData.game;

    gameStore.player = reconstructCreature(gameData.player);
    gameStore.enemy = reconstructCreature(gameData.enemy);
    gameStore.currentMonster = gameData.currentMonster;
    gameStore.isFighting = gameData.isFighting;
    gameStore.isRespawning = gameData.isRespawning;
    gameStore.respawnTimer = gameData.respawnTimer;
    gameStore.respawnTime = gameData.respawnTime;

    const inventoryStore = useInventoryStore();
    const inventoryData = saveData.inventory;
    inventoryStore.materials = inventoryData.materials || {};
    inventoryStore.items = (inventoryData.items || []).map(reconstructWeapon);
    inventoryStore.consolidateItems();

    const workflowStore = useWorkflowStore();
    const workflowData = saveData.workflow;
    if (workflowData) {
      workflowStore.workflows = workflowData.workflows || [];
      workflowStore.smithingQueue = workflowData.smithingQueue || [];
      workflowStore.smeltingQueue = (workflowData.smeltingQueue || []).map(
        reconstructWeapon
      );
    }

    const upgradeStore = useUpgradeStore();
    const upgradeData = saveData.upgrades;
    if (upgradeData) {
      upgradeStore.purchased = upgradeData.purchased || [];
    }

    return true;
  } catch {
    deleteSave();
    return false;
  }
}

export function hasSave() {
  return localStorage.getItem(SAVE_KEY) !== null;
}

export function deleteSave() {
  localStorage.removeItem(SAVE_KEY);
}
